import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiMoreVertical } from 'react-icons/fi';
import './MoreMenu.css';

const PANEL_GAP = 6;
const VIEW_MARGIN = 8;

/**
 * Three-dots overflow menu for list/card row actions.
 * items: { id, label, icon?, onSelect, danger?, disabled?, hidden? }
 */
export default function MoreMenu({
  label = 'Actions',
  items = [],
  align = 'end',
  size = 'md',
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const menuId = useId();
  const visibleItems = (items || []).filter((item) => item && !item.hidden);

  // Prefer CSS edge anchoring (right/left) so alignment does not depend on guessed panel width.
  const measure = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const panelWidth = panel?.offsetWidth || 0;
    const panelHeight = panel?.offsetHeight || 0;

    let top = rect.bottom + PANEL_GAP;
    if (panelHeight > 0 && top + panelHeight > vh - VIEW_MARGIN) {
      const above = rect.top - PANEL_GAP - panelHeight;
      if (above >= VIEW_MARGIN) {
        top = above;
      } else {
        top = Math.max(VIEW_MARGIN, Math.min(top, vh - panelHeight - VIEW_MARGIN));
      }
    }

    if (align === 'start') {
      let left = rect.left;
      if (panelWidth > 0) {
        left = Math.min(Math.max(VIEW_MARGIN, left), vw - panelWidth - VIEW_MARGIN);
      }
      setPosition({ top, left, right: 'auto' });
      return;
    }

    // align === 'end' — pin panel's right edge to trigger's right edge
    let right = vw - rect.right;
    if (panelWidth > 0) {
      const leftEdge = rect.right - panelWidth;
      if (leftEdge < VIEW_MARGIN) {
        right = Math.max(VIEW_MARGIN, vw - panelWidth - VIEW_MARGIN);
      }
    }
    setPosition({ top, left: 'auto', right });
  }, [align]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    measure();
    let frame2 = 0;
    const frame1 = window.requestAnimationFrame(() => {
      measure();
      frame2 = window.requestAnimationFrame(() => measure());
    });
    return () => {
      window.cancelAnimationFrame(frame1);
      window.cancelAnimationFrame(frame2);
    };
  }, [open, measure, visibleItems.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReflow = () => measure();
    const onScrollClose = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onScrollClose, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onScrollClose, true);
    };
  }, [open, measure]);

  if (visibleItems.length === 0) return null;

  const panelStyle = position
    ? {
        top: position.top,
        left: position.left,
        right: position.right,
        visibility: 'visible',
      }
    : {
        top: 0,
        left: align === 'start' ? 0 : 'auto',
        right: align === 'start' ? 'auto' : 0,
        visibility: 'hidden',
      };

  return (
    <div
      className={`more-menu ${className}`.trim()}
      ref={wrapRef}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        ref={triggerRef}
        className={`more-menu-trigger more-menu-trigger--${size}${open ? ' is-open' : ''}`}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <FiMoreVertical aria-hidden />
      </button>
      {open
        ? createPortal(
            <div
              id={menuId}
              ref={panelRef}
              className={`more-menu-panel more-menu-panel--${align}`}
              role="menu"
              style={panelStyle}
              onClick={(e) => e.stopPropagation()}
            >
              {visibleItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="menuitem"
                    className={`more-menu-item${item.danger ? ' more-menu-item--danger' : ''}`}
                    disabled={item.disabled}
                    onClick={() => {
                      setOpen(false);
                      item.onSelect?.();
                    }}
                  >
                    {Icon ? <Icon aria-hidden /> : null}
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
